<template>
  <div
    class="col-12"
    data-cy="application-form-view"
    :data-view-ready="!isBusy && (!objectId || object !== null)">
    <div class="row">
      <!-- Decision for application form type -->
      <div
        v-if="!accountId && !isBusy"
        class="col-12">
        <coozzy-card
          class="text-center">
          <coozzy-button
            class="mr-3 full-opa"
            data-cy="application-form-private-person-button"
            :class="applicationFormType === 'individual' ? 'primary' : ''"
            :disabled="applicationFormType === 'individual'"
            @click="setApplicationFormType('individual')">
            {{ $t('message.generic.private') }}
          </coozzy-button>
          <coozzy-button
            class="full-opa"
            data-cy="application-form-company-button"
            :class="applicationFormType === 'company' ? 'primary' : ''"
            :disabled="applicationFormType === 'company'"
            @click="setApplicationFormType('company')">
            {{ $t('message.generic.company') }}
          </coozzy-button>
        </coozzy-card>
      </div>

      <!-- Individual application form -->
      <div
        v-if="applicationFormType === 'individual'"
        class="col-12">
        <coozzy-card>
          <application-form-individual
            ref="individual"
            :is-busy="isBusy"
            :individual-id="individuals[0].internalId"
            :sending="sending"
            :object="object"
            @address-changed="onAddressChangeSnapshot" />
        </coozzy-card>
        <coozzy-card v-if="!isBusy">
          <application-form-individual-misc
            v-if="object"
            ref="individual-misc"
            :is-main-individual="true"
            :individual-id="individuals[0].internalId"
            :object="object"
            :sending="sending" />
        </coozzy-card>
        <coozzy-card v-if="!isBusy && object && object.acceptsCreditTrust && object.category !== 'PARKING_SPACE' && object.category !== 'SECONDARY_ROOM'">
          <application-form-individual-credit-trust
            v-if="object && object.acceptsCreditTrust"
            ref="individual-credit-trust"
            :individual-id="individuals[0].internalId"
            @order-certificate="orderCertificate" />
        </coozzy-card>
        <coozzy-card v-if="!isBusy">
          <application-form-individual-documents
            :individual-id="individuals[0].internalId"
            :object="object"
            :is-busy="isBusy" />
        </coozzy-card>
        <template v-if="individuals.length > 1 && !isBusy">
          <coozzy-card
            v-for="(individual, individualIndex) of individuals.slice(1)"
            :id="`individual-${individualIndex}`"
            :ref="`individual-${individualIndex}`"
            :key="individual.internalId"
            data-cy="application-form-additional-tenant-card">
            <div class="col">
              <h4
                class="display_ib"
                data-cy="application-form-additional-tenant-title">
                {{ $t('message.applicationForm.additionalTenant') }}
              </h4>
              <coozzy-button
                design="transparent"
                class="float-right shadow-none"
                data-cy="application-form-delete-additional-tenant-button"
                @click="deleteIndividual(individual)">
                <coozzy-delete-icon />
              </coozzy-button>
            </div>
            <hr>
            <application-form-individual
              v-if="!isBusy"
              ref="individual-tenants"
              :individual-id="individual.internalId"
              :sending="sending"
              :object="object"
              :email-main-tenant="emailMainTenant"
              :changed-address="addressChangeSnapshot" />
            <hr v-if="object && object.acceptsCreditTrust">
            <application-form-individual-credit-trust
              v-if="object && object.acceptsCreditTrust"
              ref="sub-individual-credit-trust"
              :individual-id="individual.internalId"
              @order-certificate="orderCertificate" />
            <hr v-if="object">
            <application-form-individual-misc
              v-if="object"
              ref="sub-individual-misc"
              :key="individualIndex"
              :is-main-individual="false"
              :object="object"
              :individual-id="individual.internalId"
              :sending="sending" />
            <hr>
            <application-form-individual-documents
              :individual-id="individual.internalId"
              :object="object"
              :is-busy="isBusy" />
          </coozzy-card>
        </template>
        <coozzy-card v-if="!isBusy">
          <coozzy-button
            class="my-3 mr-2"
            data-cy="application-form-add-additional-tenant-button"
            design="prop-green"
            @click="addIndividual">
            {{ $t('message.applicationForm.addTenant') }}
          </coozzy-button>
          <coozzy-button
            class="my-3"
            design="prop-green"
            data-cy="application-form-add-guarantor-button"
            @click="addGuarantor">
            {{ $t('message.applicationForm.addGuarantor') }}
          </coozzy-button>
        </coozzy-card>
        <!-- Guarantor cards -->
        <template v-if="guarantors.length > 0 && !isBusy">
          <coozzy-card
            v-for="(guarantor, guarantorIndex) of guarantors"
            :id="`guarantor-${guarantorIndex}`"
            :ref="`guarantor-${guarantorIndex}`"
            :key="'guarantor-' + guarantor.internalId"
            data-cy="application-form-guarantor-card">
            <div class="col">
              <h4
                class="display_ib"
                data-cy="application-form-guarantor-title">
                {{ $t('message.applicationForm.guarantorTitle') }}
              </h4>
              <coozzy-button
                design="transparent"
                class="float-right shadow-none"
                data-cy="application-form-delete-guarantor-button"
                @click="deleteGuarantor(guarantor)">
                <coozzy-delete-icon />
              </coozzy-button>
            </div>
            <hr>
            <application-form-individual
              v-if="!isBusy"
              ref="guarantor-individuals"
              :individual-id="guarantor.internalId"
              :sending="sending"
              :object="object"
              :email-main-tenant="emailMainTenant"
              :changed-address="addressChangeSnapshot"
              :is-guarantor="true" />
            <hr v-if="object && object.acceptsCreditTrust">
            <application-form-individual-credit-trust
              v-if="object && object.acceptsCreditTrust"
              ref="guarantor-credit-trust"
              :individual-id="guarantor.internalId"
              @order-certificate="orderCertificate" />
            <hr>
            <application-form-individual-documents
              :individual-id="guarantor.internalId"
              :object="object"
              :is-busy="isBusy" />
          </coozzy-card>
        </template>
      </div>

      <!-- Company application form -->
      <div
        v-else-if="applicationFormType === 'company'"
        class="col-12">
        <coozzy-card>
          <application-form-company
            ref="company"
            :company="company"
            :is-busy="isBusy"
            :sending="sending"
            :object="object" />
        </coozzy-card>
        <coozzy-card v-if="!isBusy">
          <application-form-company-misc
            v-if="object"
            ref="company-misc"
            :company="company"
            :is-busy="isBusy"
            :object="object"
            :sending="sending" />
        </coozzy-card>
        <coozzy-card v-if="!isBusy">
          <application-form-company-documents :company="company" />
        </coozzy-card>
      </div>
      <div class="col-12">
        <template v-if="!isBusy">
          <application-form-preferred-deposit
            v-if="applicationFormType && sending && object?.deposit?.depositNeeded && (object?.deposit?.bankGuaranteeAccepted || object?.deposit?.bankDepotAccepted || object?.deposit?.cashPaymentAccepted || object?.deposit?.collectiveInsuranceAccepted || object?.deposit?.cooperativeShareAccepted || object?.deposit?.insuranceAccepted)"
            ref="preferred-deposit"
            :object="object"
            :is-busy="isBusy"
            :company="company"
            :marketing-account="marketingAccount"
            :individual="individuals[0]"
            :sending="sending" />
        </template>
      </div>

      <div class="col-12">
        <template v-if="!isBusy">
          <application-form-legal-notice
            v-if="applicationFormType && sending"
            ref="legal"
            :object-id="objectId" />
        </template>
        <span
          style="line-height: 28px;"
          data-cy="application-form-contact-footer">{{ $t('message.applicationForm.anyQuestion', {
            phoneNumberMarketerAccount: object?.marketingAssignee?.phone ? object.marketingAssignee.phone : marketingAccount && marketingAccount.company ? marketingAccount.company.phoneNumber : '',
            emailMarketerAccount: object?.marketingAssignee?.email ? object.marketingAssignee.email : marketingAccount && marketingAccount.company ? marketingAccount.company.email : '' }) }}</span>
        <coozzy-button
          design="primary"
          class="float-right px-5 mb-3"
          data-cy="application-form-send-button"
          @click="save">
          <template v-if="sending">
            {{ $t('message.generic.form.send') }}
          </template>
          <template v-else>
            {{ $t('message.generic.form.save') }}
          </template>
        </coozzy-button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import CoozzyCard from '@/framework/components/card/CoozzyCard.vue'
import CoozzyButton from '@/framework/components/button/CoozzyButton.vue'
import ApplicationFormIndividual from '@/marketingBasic/components/applicationForm/ApplicationFormIndividual.vue'
import ApplicationFormCompany from '@/marketingBasic/components/applicationForm/ApplicationFormCompany.vue'
import { TypeDate } from '@/types/api'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import ApplicationFormIndividualMisc from '@/marketingBasic/components/applicationForm/ApplicationFormIndividualMisc.vue'
import ApplicationFormCompanyMisc from '@/marketingBasic/components/applicationForm/ApplicationFormCompanyMisc.vue'
import RequestApi from '@/marketingBasic/api/RequestApi'
import ObjectApi from '@/misc/apis/ObjectApi'
import UserApi from '@/misc/apis/UserApi'
import ApplicationFormCompanyDocuments from '@/marketingBasic/components/applicationForm/ApplicationFormCompanyDocuments.vue'
import ApplicationFormIndividualDocuments from '@/marketingBasic/components/applicationForm/ApplicationFormIndividualDocuments.vue'
import { user } from '@/mixins/user'
import CoozzyDeleteIcon from '@/framework/components/icons/CoozzyDeleteIcon.vue'
import ApplicationFormLegalNotice from '@/marketingBasic/components/applicationForm/ApplicationFormLegalNotice.vue'
import ApplicationFormPreferredDeposit from '@/marketingBasic/components/applicationForm/ApplicationFormPreferredDeposit.vue'
import * as Sentry from '@sentry/vue'
import ApplicationFormIndividualCreditTrust from '@/marketingBasic/components/applicationForm/ApplicationFormIndividualCreditTrust.vue'
import type { ApplicationFormCompany as ApplicationFormCompanyModel } from '@/types/models'
import {
  ApplicationFormPreferredDepositMarketingAccount,
  ApplicationFormRootMarketingObject,
  ApplicationFormStoreIndividual
} from '@/types/models'

type IndividualShellRef = Vue & {
  individualId: string
  isInvalid: () => boolean
  syncDateFieldsFromInputs?: () => void
}
type CreditTrustRef = Vue & { individualId: string; orderCertificate: () => Promise<void> }
type MiscRef = { isInvalid: () => boolean }
type LegalRef = { isInvalid: () => boolean }
type CompanyShellRef = { isInvalid: () => boolean }
type CompanyEmploymentRef = { isInvalidEmail: () => boolean }

function asRefArray<T>(ref: T | T[] | undefined): T[] {
  if (!ref) return []
  return Array.isArray(ref) ? ref : [ref]
}

export default Vue.extend({
  name: 'ApplicationForm',
  components: {
    ApplicationFormIndividualCreditTrust,
    ApplicationFormLegalNotice,
    ApplicationFormPreferredDeposit,
    ApplicationFormIndividualMisc,
    ApplicationFormCompanyMisc,
    ApplicationFormIndividual,
    ApplicationFormCompany,
    CoozzyButton,
    CoozzyCard,
    CoozzyDeleteIcon,
    ApplicationFormCompanyDocuments,
    ApplicationFormIndividualDocuments
  },
  mixins: [user],
  props: {
    sending: {
      type: Boolean,
      default: false
    },
    objectId: {
      type: String,
      default: ''
    },
    requestId: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      addressChangeSnapshot: '',
      applicationFormDataInvalidCaptured: false,
      isBusy: false,
      marketingAccount: null as ApplicationFormPreferredDepositMarketingAccount | null,
      object: null as ApplicationFormRootMarketingObject | null
    }
  },
  computed: {
    applicationFormType(): string {
      return this.$store.state.applicationForm.applicationFormType as string
    },
    viewingDone(): boolean {
      return this.$store.state.applicationForm.viewingDone as boolean
    },
    desiredStartOfContract(): TypeDate | null {
      return this.$store.state.applicationForm.desiredStartOfContract as TypeDate | null
    },
    ...mapGetters('applicationForm', ['getPreferredLanguage']),
    individuals(): ApplicationFormStoreIndividual[] {
      return this.$store.state.applicationForm.individuals as ApplicationFormStoreIndividual[]
    },
    guarantors(): ApplicationFormStoreIndividual[] {
      return this.$store.state.applicationForm.guarantors as ApplicationFormStoreIndividual[]
    },
    company(): ApplicationFormCompanyModel | null {
      return this.$store.state.applicationForm.company as ApplicationFormCompanyModel | null
    },
    emailMainTenant(): string {
      return this.individuals.length > 0 && this.individuals[0].email ? this.individuals[0].email : ''
    }
  },
  mounted() {
    if (this.objectId) {
      ObjectApi.getMarketingObject(this.objectId)
        .then(response => {
          this.object = response as ApplicationFormRootMarketingObject
          if (this.object.marketingAccountId !== '-1') {
            UserApi.getAccount(this.object.marketingAccountId)
              .then(res => {
                this.marketingAccount = res.account as ApplicationFormPreferredDepositMarketingAccount
              })
              .catch(e => {
                console.log(e)
                Vue.toasted.show(this.$t('message.loadingErrors.user'), { type: 'error' })
              })
          }
        })
        .catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.loadingErrors.object'), { type: 'error' })
        })
    }
    if (this.userEmail) {
      this.isBusy = true
      RequestApi.getApplicationFormByEmail(this.userEmail)
        .then(response => {
          this.loadApplicationForm(response.application)
        })
        .catch(() => {})
        .finally(() => {
          this.isBusy = false
        })
    }
  },
  methods: {
    ...mapActions('applicationForm', ['setApplicationFormType', 'loadApplicationForm', 'addDefaultIndividual', 'addDefaultGuarantor']),
    ...mapMutations('applicationForm', ['removeIndividual', 'removeGuarantor']),
    addIndividual() {
      this.addDefaultIndividual()
      this.$nextTick(() => {
        const indexIndividual = this.individuals.length - 2
        const scrollTarget = this.$el.querySelector('#individual-' + indexIndividual)
        scrollTarget?.scrollIntoView(true)
      })
    },
    deleteIndividual(individual: ApplicationFormStoreIndividual) {
      this.removeIndividual(individual)
    },
    addGuarantor() {
      this.addDefaultGuarantor()
      this.$nextTick(() => {
        const indexGuarantor = this.guarantors.length - 1
        const scrollTarget = this.$el.querySelector('#guarantor-' + indexGuarantor)
        if (scrollTarget) {
          scrollTarget.scrollIntoView(true)
        }
      })
    },
    deleteGuarantor(guarantor: ApplicationFormStoreIndividual) {
      this.removeGuarantor(guarantor)
    },
    onAddressChangeSnapshot(val: string) {
      this.addressChangeSnapshot = val
    },
    orderCertificate(individualId: string) {
      const mainIndividual = this.$refs.individual as IndividualShellRef | undefined
      if (mainIndividual) {
        if (mainIndividual.individualId === individualId) {
          if (!mainIndividual.isInvalid()) {
            const mainCredit = this.$refs['individual-credit-trust'] as CreditTrustRef | undefined
            if (mainCredit) {
              mainCredit.orderCertificate().catch(() => {})
            }
          }
        }
        asRefArray(this.$refs['individual-tenants'] as IndividualShellRef | undefined).forEach(
          element => {
            if (element.individualId === individualId) {
              if (!element.isInvalid()) {
                asRefArray(
                  this.$refs['sub-individual-credit-trust'] as CreditTrustRef | undefined
                ).forEach(subCreditTrustComponent => {
                  if (subCreditTrustComponent.individualId === individualId) {
                    subCreditTrustComponent.orderCertificate().catch(() => {})
                  }
                })
              }
            }
          }
        )
        asRefArray(this.$refs['guarantor-individuals'] as IndividualShellRef | undefined).forEach(
          element => {
            if (element.individualId === individualId) {
              if (!element.isInvalid()) {
                asRefArray(
                  this.$refs['guarantor-credit-trust'] as CreditTrustRef | undefined
                ).forEach(subCreditTrustComponent => {
                  if (subCreditTrustComponent.individualId === individualId) {
                    subCreditTrustComponent.orderCertificate().catch(() => {})
                  }
                })
              }
            }
          }
        )
      }
    },
    syncApplicationDateFields() {
      const shells = [
        this.$refs.individual as IndividualShellRef | undefined,
        ...asRefArray(this.$refs['individual-tenants'] as IndividualShellRef | IndividualShellRef[] | undefined),
        ...asRefArray(this.$refs['guarantor-individuals'] as IndividualShellRef | IndividualShellRef[] | undefined)
      ]
      shells.forEach(shell => {
        shell?.syncDateFieldsFromInputs?.()
      })
    },
    save() {
      this.syncApplicationDateFields()
      let valid = true
      let sameEmailMainTenant = false
      if (this.sending) {
        const mainIndividualRef = this.$refs.individual as IndividualShellRef | undefined
        if (mainIndividualRef) {
          if (mainIndividualRef.isInvalid()) {
            valid = false
          }
          const misc = this.$refs['individual-misc'] as MiscRef | undefined
          if (misc?.isInvalid()) {
            valid = false
          }
          asRefArray(this.$refs['individual-tenants'] as IndividualShellRef | undefined).forEach(
            element => {
              if (element.isInvalid()) {
                valid = false
              }
            }
          )
          if (this.individuals.length > 1) {
            const mainTenantEmail = this.individuals[0].email
            sameEmailMainTenant = this.individuals.some((individual, index) => {
              return index > 0 && individual.email === mainTenantEmail
            })
          }
          asRefArray(this.$refs['sub-individual-misc'] as MiscRef | undefined).forEach(element => {
            if (element.isInvalid()) {
              valid = false
            }
          })
          asRefArray(this.$refs['guarantor-individuals'] as IndividualShellRef | undefined).forEach(
            element => {
              if (element.isInvalid()) {
                valid = false
              }
            }
          )
        } else {
          const companyRef = this.$refs.company as CompanyShellRef | undefined
          if (companyRef?.isInvalid()) {
            valid = false
          }
          const companyMisc = this.$refs['company-misc'] as MiscRef | undefined
          if (companyMisc?.isInvalid()) {
            valid = false
          }
        }

        const legal = this.$refs.legal as LegalRef | undefined
        if (legal?.isInvalid()) {
          valid = false
        }
      } else {
        if (this.$refs.individual) {
          if (!this.individuals[0].email) {
            this.individuals[0].email = this.userEmail as string
          }
        } else {
          const companyForm = this.$refs.company as Vue & {
            $refs: { contactPerson: CompanyEmploymentRef }
          }
          if (companyForm?.$refs?.contactPerson?.isInvalidEmail()) {
            valid = false
          }
        }
      }
      if (this.individuals.length > 0) {
        Sentry.setUser({ email: this.individuals[0].email })
        Sentry.setExtra('individual', this.individuals[0])
        Sentry.setExtra('individualAddress', this.individuals[0]?.address)
        Sentry.setExtra('individualHousegold', this.individuals[0]?.household)
        Sentry.setExtra('individualEmployment', this.individuals[0]?.employment)
        Sentry.setExtra('individualTenancy', this.individuals[0]?.tenancy)
        Sentry.setExtra('individualInsurance', this.individuals[0]?.insurance)
        Sentry.setExtra('individualLegal', this.individuals[0]?.legal)
      } else if (this.company && this.company.contactPerson) {
        Sentry.setUser({ email: this.company.contactPerson.email })
        Sentry.setExtra('company', this.company)
        Sentry.setExtra('companyAddress', this.company?.address)
        Sentry.setExtra('companyContactPerson', this.company?.contactPerson)
        Sentry.setExtra('companyInsurance', this.company?.insurance)
        Sentry.setExtra('companyLegal', this.company?.legal)
        Sentry.setExtra('companyTenancy', this.company?.tenancy)
      }

      if (!valid) {
        this.applicationFormDataInvalidCaptured = true
        Sentry.captureMessage('application form data invalid')
        Vue.toasted.show(this.$t('message.savingErrors.requiredApplicationForm'), { type: 'error' })
        this.$nextTick(() => {
          if (sameEmailMainTenant) {
            Vue.toasted.show(this.$t('message.savingErrors.sameEmailMainTenant'), { type: 'error' })
          }
        })
        return
      }

      this.isBusy = true
      RequestApi.saveApplication(this.individuals, this.company, this.guarantors)
        .then(response => {
          if (this.sending) {
            const applicationId = response.application.id
            const marketingLanguage = this.object?.marketingLanguage || 'PREFERRED_LANGUAGE_UNDEFINED'
            RequestApi.sendApplicationForm(applicationId, this.objectId, this.requestId, this.viewingDone, this.desiredStartOfContract, marketingLanguage)
              .then(() => {
                if (this.applicationFormDataInvalidCaptured) {
                  Sentry.captureMessage('application form sent after data correction')
                }
                this.isBusy = false
                this.$emit('application-sent', true)
              })
              .catch(e => {
                Sentry.captureException(e)
                console.log(e)
                this.isBusy = false
                Vue.toasted.show(this.$t('message.savingErrors.sendingApplicationForm'), { type: 'error' })
              })
          } else {
            this.isBusy = false
          }
        })
        .catch(e => {
          Sentry.captureException(e)
          console.log(e)
          this.isBusy = false
          Vue.toasted.show(this.$t('message.savingErrors.savingApplicationForm'), { type: 'error' })
        })
    }
  }
})
</script>

<style lang="scss" scoped>
.display_ib {
  display: inline-block;
}
.full-opa{
  opacity: 1 !important;
}

</style>
